import { useCallback, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { authApi } from '../api/auth'
import { setSessionLostHandler, tokenStore } from '../api/client'
import type { StoredTokens } from '../api/client'
import type { AuthResult, CurrentUser } from '../api/types'
import { AuthContext } from './AuthContext'
import type { AuthState } from './AuthContext'

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null)
  // No stored token means there is nothing to restore, so skip the bootstrap
  // phase entirely rather than flipping the flag from inside the effect.
  const [bootstrapping, setBootstrapping] = useState(() => tokenStore.read() !== null)

  const accept = useCallback((result: AuthResult) => {
    tokenStore.write({
      accessToken: result.accessToken,
      refreshToken: result.refreshToken,
      expiresAt: result.expiresAt,
    })
    setUser(result.user)
  }, [])

  /**
   * Adopts a session whose tokens arrived from outside this provider — the
   * QuickBooks callback redeems its one-time code for a token pair, but the
   * `CurrentUser` only exists server-side, so it is fetched with the new token.
   *
   * The returned promise settles only once `user` is actually in state (or the
   * session has been discarded). A caller that navigates into the guarded app
   * *before* awaiting it would race `RequireAuth`'s null-user check and get
   * bounced straight back to the login page.
   */
  const acceptSession = useCallback((session: StoredTokens) => {
    tokenStore.write(session)
    return authApi
      .me()
      .then(setUser)
      .catch((cause: unknown) => {
        // A token this fresh failing /me is not a transient problem; do not
        // leave a stored session the app cannot use. The rejection is rethrown
        // so the caller can show why the sign in did not complete.
        tokenStore.clear()
        setUser(null)
        throw cause
      })
  }, [])

  // The client clears tokens on a failed refresh; mirror that into React state
  // so the guard redirects instead of rendering a shell with no user.
  useEffect(() => {
    setSessionLostHandler(() => setUser(null))
    return () => setSessionLostHandler(null)
  }, [])

  useEffect(() => {
    let cancelled = false
    if (!tokenStore.read()) return
    authApi
      .me()
      .then((me) => {
        if (!cancelled) setUser(me)
      })
      .catch(() => {
        tokenStore.clear()
        if (!cancelled) setUser(null)
      })
      .finally(() => {
        if (!cancelled) setBootstrapping(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const login = useCallback(
    async (email: string, password: string) => {
      accept(await authApi.login(email, password))
    },
    [accept],
  )

  const register = useCallback(
    async (email: string, password: string, companyName: string) => {
      accept(await authApi.register(email, password, companyName))
    },
    [accept],
  )

  const logout = useCallback(async () => {
    await authApi.logout()
    tokenStore.clear()
    setUser(null)
  }, [])

  /** Re-reads the user after anything that moves the credit balance. */
  const refreshUser = useCallback(async () => {
    if (!tokenStore.read()) return
    try {
      setUser(await authApi.me())
    } catch {
      /* a transient failure must not sign the user out */
    }
  }, [])

  const value = useMemo<AuthState>(
    () => ({ user, bootstrapping, login, register, logout, refreshUser, acceptSession }),
    [user, bootstrapping, login, register, logout, refreshUser, acceptSession],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
